import { useTranslation } from 'react-i18next';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  Box,
  Drawer,
  IconButton,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  useTheme,
} from '@monorepo/nova-ui';
import { BalanceButton } from '../balance-button';
import {
  ProfileMenuOption,
  ProfileMenuOptionValue,
} from './profile-menu.types';
import { StyledMenuItemThemeSwitch } from './styled/profile-menu.styled';

interface Props {
  open: boolean;
  onClose: () => void;
  options: ProfileMenuOption[];
  userId?: string;
}

export const ProfileMenuDrawer = ({
  open,
  onClose,
  options,
  userId,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation('hubCommon');

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 326,
          height: '100%',
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            height: 58,
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.background.default,
            borderBottom: '4px solid',
            borderColor: theme.palette.background.contrast,
            p: [4.5, 6, 3.5, 6],
          }}
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
            <IconButton onClick={onClose} sx={{ width: 24, height: 24 }}>
              <SystemIcon
                color={theme.palette.text.main}
                iconName="CaretLeft"
                sx={{ height: 20 }}
              />
            </IconButton>
            <Typography
              variant="h6"
              color={theme.palette.text.main}
              sx={{ pt: 0.5, fontWeight: 600 }}
            >
              {t('profile')}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ width: 16, height: 16, p: 0 }}>
            <SystemIcon
              color={theme.palette.text.main}
              iconName="X"
              sx={{ width: 16, height: 16 }}
            />
          </IconButton>
        </Box>

        {/* Menu options */}
        <Box
          sx={{
            flex: 1,
            p: 4,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <MenuList sx={{ p: 0 }}>
            {options
              .filter((option) => !option.hidden)
              .map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={option.onClick}
                  sx={{
                    height: 58,
                    maxHeight: 58,
                    borderBottom: '1px solid',
                    borderColor: theme.palette.text.fifth,
                    p: 4,
                  }}
                >
                  {option.leftComponent && (
                    <StyledMenuItemThemeSwitch>
                      {option.leftComponent}
                    </StyledMenuItemThemeSwitch>
                  )}
                  {option.icon && (
                    <IconButton sx={{ p: 0, mr: 3 }}>{option.icon}</IconButton>
                  )}
                  {option.label}
                  {option.value === ProfileMenuOptionValue.Profile && (
                    <SystemIcon
                      iconName="ArrowRightMenu"
                      color={theme.palette.text.third}
                      viewBox="0 0 34 34"
                      size={34}
                      sx={{ ml: 'auto' }}
                    />
                  )}
                </MenuItem>
              ))}
          </MenuList>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            height: 72,
            justifyContent: 'flex-start',
            alignItems: 'center',
            backgroundColor: theme.palette.background.default,
            borderTop: '4px solid',
            borderColor: theme.palette.background.contrast,
            pt: 3.5,
            px: 4,
            pb: 4.5,
          }}
        >
          <BalanceButton userId={userId} />
        </Box>
      </Box>
    </Drawer>
  );
};
